import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { BarChart as BarChartIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { enhancedApi } from '../api';
import { AuthContext } from '../components/AuthContext';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import ReportPeriodToolbar from '../components/reports/ReportPeriodToolbar';
import { rangeFromPreset } from '../components/reports/reportDatePresets';

dayjs.extend(quarterOfYear);

const fmtCurrency = (n) =>
  n != null && !isNaN(n) ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const fmtPercent = (n) => (n != null && !isNaN(n) ? `${Number(n).toFixed(1)}%` : '—');

/** Data Grid v7+ passes the raw value; `{ value }` breaks when arg is null or a primitive. */
const cellVal = (p) => {
  if (p == null) return p;
  if (typeof p === 'object' && 'value' in p) return p.value;
  return p;
};

function SummaryCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  );
}

const ProfitabilityReport = () => {
  const theme = useTheme();
  const { userRole } = useContext(AuthContext) || {};
  const noBilling = (userRole || localStorage.getItem('userRole') || '').includes('no_billing');

  const [datePreset, setDatePreset] = useState('this_month');
  const [dateFrom, setDateFrom] = useState(() => rangeFromPreset('this_month').from);
  const [dateTo, setDateTo] = useState(() => rangeFromPreset('this_month').to);
  const [groupBy, setGroupBy] = useState('client');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyPreset = useCallback((preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const { from, to } = rangeFromPreset(preset);
      setDateFrom(from);
      setDateTo(to);
    }
  }, []);

  const fetchReport = useCallback(() => {
    setLoading(true);
    setError(null);
    enhancedApi
      .getProfitabilityReport({
        date_from: dateFrom.format('YYYY-MM-DD'),
        date_to: dateTo.format('YYYY-MM-DD'),
        group_by: groupBy,
      })
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load report'))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, groupBy]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const rawItems = data?.results ?? data?.rows ?? [];

  const rows = useMemo(() => {
    return rawItems.map((r, i) => {
      const revenue = r.revenue ?? 0;
      const cost = r.cost ?? 0;
      const profit = r.profit ?? revenue - cost;
      const marginPct = r.margin != null ? r.margin : revenue ? (profit / revenue) * 100 : null;
      const hours = r.hours != null ? Number(r.hours) : null;
      const avgRate = hours && hours > 0 ? revenue / hours : null;
      return {
        id: r.id ?? i,
        name: r.name ?? r.display_name ?? '—',
        revenue,
        cost,
        profit,
        margin: marginPct,
        hours,
        avgRate,
        trend: r.trend_vs_prior ?? r.trend ?? 0,
      };
    });
  }, [rawItems]);

  const summary = useMemo(() => {
    let totalRev = 0;
    let totalProfit = 0;
    let hoursSum = 0;
    let hoursKnown = false;
    rows.forEach((r) => {
      totalRev += r.revenue;
      if (!noBilling) totalProfit += r.profit;
      if (r.hours != null && !isNaN(r.hours)) {
        hoursSum += r.hours;
        hoursKnown = true;
      }
    });
    const avgRateHr = hoursKnown && hoursSum > 0 ? totalRev / hoursSum : null;
    const marginAgg = !noBilling && totalRev > 0 ? (totalProfit / totalRev) * 100 : null;
    return {
      totalRevenue: totalRev,
      totalHours: hoursKnown ? hoursSum : null,
      avgRateHr: avgRateHr,
      marginPct: marginAgg,
    };
  }, [rows, noBilling]);

  const chartData = useMemo(() => {
    const sorted = [...rows].sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0)).slice(0, 12);
    return sorted.map((r) => ({ name: r.name, revenue: r.revenue ?? 0 }));
  }, [rows]);

  const handleExportCsv = () => {
    const cols = noBilling
      ? ['Name', 'Hours', 'Revenue', 'Avg Rate/Hr']
      : ['Name', 'Hours', 'Revenue', 'Avg Rate/Hr', 'Margin %'];
    const rowToCells = (r) => {
      const ar = r.avgRate != null ? r.avgRate.toFixed(2) : '';
      const h = r.hours != null ? r.hours.toFixed(1) : '';
      return noBilling
        ? [r.name, h, r.revenue, ar]
        : [r.name, h, r.revenue, ar, r.margin != null ? r.margin.toFixed(1) : ''];
    };
    const header = cols.join(',');
    const body = rows
      .map((r) => rowToCells(r).map((c) => (typeof c === 'string' && c.includes(',') ? `"${c}"` : c)).join(','))
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profitability-report-${dateFrom.format('YYYY-MM-DD')}-${dateTo.format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(() => {
    const base = [
      { field: 'name', headerName: groupBy === 'client' ? 'Client' : 'Name', flex: 1, minWidth: 160 },
      {
        field: 'hours',
        headerName: 'Hours',
        width: 100,
        valueFormatter: (p) => {
          const v = cellVal(p);
          return v != null && !isNaN(v) ? Number(v).toFixed(1) : '—';
        },
      },
      { field: 'revenue', headerName: 'Revenue', width: 130, valueFormatter: (p) => fmtCurrency(cellVal(p)) },
      {
        field: 'avgRate',
        headerName: 'Avg Rate',
        width: 120,
        valueFormatter: (p) => fmtCurrency(cellVal(p)),
      },
    ];
    if (noBilling) return base;
    return [...base, { field: 'margin', headerName: 'Margin %', width: 110, valueFormatter: (p) => fmtPercent(cellVal(p)) }];
  }, [groupBy, noBilling]);

  const empty = !loading && !error && rows.length === 0;

  const groupToggle = (
    <ToggleButtonGroup
      value={groupBy}
      exclusive
      size="small"
      onChange={(_, v) => v != null && setGroupBy(v)}
    >
      <ToggleButton value="client" sx={{ textTransform: 'none' }}>
        Client
      </ToggleButton>
      <ToggleButton value="service" sx={{ textTransform: 'none' }}>
        Service
      </ToggleButton>
      <ToggleButton value="user" sx={{ textTransform: 'none' }}>
        Staff
      </ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <Box>
      <ReportPeriodToolbar
        title="Profitability"
        subtitle="Paid invoice revenue, costs, and margin for the selected period."
        datePreset={datePreset}
        onDatePresetChange={(id) => {
          applyPreset(id);
        }}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={(v) => {
          if (v) {
            setDateFrom(v);
            setDatePreset('custom');
          }
        }}
        onDateToChange={(v) => {
          if (v) {
            setDateTo(v);
            setDatePreset('custom');
          }
        }}
        onExport={handleExportCsv}
        exportDisabled={loading || empty}
        extra={groupToggle}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={340} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
        </>
      ) : empty ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            borderColor: 'divider',
            borderStyle: 'dashed',
          }}
        >
          <BarChartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            No data for selected period
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try another range ({dateFrom.format('MMM D, YYYY')} – {dateTo.format('MMM D, YYYY')}) or a different grouping.
          </Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            <SummaryCard label="Total Revenue" value={fmtCurrency(summary.totalRevenue)} />
            <SummaryCard
              label="Total Hours"
              value={summary.totalHours != null ? `${summary.totalHours.toFixed(1)} hrs` : '—'}
            />
            <SummaryCard label="Avg Rate/Hr" value={fmtCurrency(summary.avgRateHr)} />
            <SummaryCard
              label="Profit Margin"
              value={noBilling ? '—' : fmtPercent(summary.marginPct)}
            />
          </Box>

          {chartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'divider', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Revenue by {groupBy === 'client' ? 'client' : groupBy === 'service' ? 'service' : 'staff'}
              </Typography>
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-28} textAnchor="end" height={72} />
                    <YAxis tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                    <Tooltip formatter={(v) => [fmtCurrency(v), 'Revenue']} />
                    <Bar dataKey="revenue" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ borderColor: 'divider', overflow: 'hidden' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              sx={{ border: 'none', '& .MuiDataGrid-cell': { borderColor: 'divider' } }}
              autoHeight
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ProfitabilityReport;
